import type { StreamEntry } from "./history/types.js";
import { seqsOf } from "./history/rows.js";
import { screenRows } from "./transcript-journal.js";

// Why a hydrate may drop rows on purpose (a rewind, a hole too wide); any other drop is a loss.
export function deliberateDrop(
  mode: "refresh" | "rewind" | undefined,
  before: ReadonlySet<number>,
  pageFloor: number | undefined,
): string | undefined {
  if (mode === "rewind") return "rewind";
  if (pageFloor === undefined) return undefined;
  let held = -1;
  for (const seq of before) if (seq < pageFloor && seq > held) held = seq;
  return held >= 0 && pageFloor - held - 1 > HOLE_KEEP_MAX_ROWS ? "hole-too-wide" : undefined;
}

// Past the gap read-back budget (GAP_READBACK_MAX_ROWS) a hole is scroll-back, never kept.
export const HOLE_KEEP_MAX_ROWS = 30_000;
// Under a page over a disk or mirror paint, whose age is no loss: one read's worth is worth closing.
export const PREVIEW_HOLE_KEEP_MAX_ROWS = 200;

// Whether the hole between the held rows and a hydrated page is the server's own cut. Every
// hole is read back whole; a cut is not reported as a timeline gap. Only `budgetCut` (the
// server's byte budget dropped rows below the page, T-09) says so: a byte cut that opens inside
// a reply sets it too. `headPartial` alone (snap bound, retention, #3239) still reports.
export interface PageCutMarks {
  headPartial?: boolean;
  budgetCut?: boolean;
}
export function pageHoleIsServerCut(page: PageCutMarks | undefined): boolean {
  return page?.budgetCut === true;
}

// The entries strictly below a page's lowest seq; across a hole they stay too, `hole` names it.
export function rowsBelowPageFloor(
  entries: readonly StreamEntry[],
  items: Record<string, unknown>[],
): { rows: readonly StreamEntry[]; hole?: { after: number; before: number } } {
  let pageFloor: number | undefined;
  for (const raw of items) {
    const seq = (raw as { seq?: unknown }).seq;
    if (typeof seq === "number" && (pageFloor === undefined || seq < pageFloor)) pageFloor = seq;
  }
  if (pageFloor === undefined || pageFloor <= 1) return { rows: [] };
  let held = -1;
  for (const entry of entries) {
    if (typeof entry.seq === "number" && entry.seq > held) held = entry.seq;
  }
  const rows = entries.filter((entry) => typeof entry.seq === "number" && entry.seq < pageFloor);
  if (held < 0 || held >= pageFloor - 1) return { rows };
  if (pageFloor - held - 1 > HOLE_KEEP_MAX_ROWS) return { rows: [] };
  const onPage = new Set(
    items.map((raw) => (raw as { messageId?: unknown }).messageId).filter((id) => id !== undefined),
  );
  return {
    rows: rows.filter(
      (e) => !(e.kind === "text" && e.messageId !== undefined && onPage.has(e.messageId)),
    ),
    hole: { after: held, before: pageFloor },
  };
}

// Committed rows at or above a page's floor it lacks: a lossy page, merged, never the view (T13).
export function committedRowsMissingFromPage(
  entries: readonly StreamEntry[],
  items: readonly Record<string, unknown>[],
): number {
  const onPage = new Set<number>();
  for (const raw of items) if (typeof raw.seq === "number") onPage.add(raw.seq);
  if (onPage.size === 0) return 0;
  const floor = Math.min(...onPage);
  let missing = 0;
  for (const seq of screenRows(entries, seqsOf)) if (seq >= floor && !onPage.has(seq)) missing += 1;
  return missing;
}
